export interface InventoryLocationSnapshot {
  id: string;
  name: string;
  available: number;
}

export const AVAILABLE_STOCK_SOURCE = "available_stock_rule_v2";

export function isManagedInventorySource(source?: string): boolean {
  return source === "shopify_allocation_v1" || source === AVAILABLE_STOCK_SOURCE;
}

export interface InventoryConsumptionSnapshot {
  id: string;
  name: string;
  quantity: number;
}

export interface InventoryStockColumn {
  id: string;
  name: string;
  isTotal: boolean;
}

export function parseInventoryLocationSnapshot(value: string): InventoryLocationSnapshot[] {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((entry) => {
      if (typeof entry !== "object" || entry === null) return [];
      const candidate = entry as Record<string, unknown>;
      if (
        typeof candidate.id !== "string"
        || typeof candidate.name !== "string"
        || typeof candidate.available !== "number"
        || !Number.isInteger(candidate.available)
      ) return [];
      return [{
        id: candidate.id,
        name: candidate.name.trim(),
        available: candidate.available,
      }];
    });
  } catch {
    return [];
  }
}

export function parseInventoryConsumptionSnapshot(
  value: string,
): InventoryConsumptionSnapshot[] {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((entry) => {
      if (typeof entry !== "object" || entry === null) return [];
      const candidate = entry as Record<string, unknown>;
      if (
        typeof candidate.id !== "string"
        || typeof candidate.name !== "string"
        || typeof candidate.quantity !== "number"
        || !Number.isInteger(candidate.quantity)
      ) return [];
      return [{
        id: candidate.id,
        name: candidate.name.trim(),
        quantity: candidate.quantity,
      }];
    });
  } catch {
    return [];
  }
}

export function inventoryLocationDisplayName(value: string): string {
  const name = value.trim();
  if (name.includes("大阪")) return "大阪";
  if (name.includes("東京")) return "東京";
  return name;
}

export function requiresInventoryReturn(
  salesSource: string,
  locations: readonly InventoryConsumptionSnapshot[],
): boolean {
  const sourceLocation = inventoryLocationDisplayName(salesSource);
  if (!sourceLocation || sourceLocation === "ネット") return false;
  return locations.some((location) => (
    Number.isInteger(location.quantity)
    && location.quantity > 0
    && inventoryLocationDisplayName(location.name) !== sourceLocation
  ));
}

export function inventoryStockColumns(
  variants: ReadonlyArray<{ locations: readonly InventoryLocationSnapshot[] }>,
): InventoryStockColumn[] {
  const locations = new Map<string, string>();
  for (const variant of variants) {
    for (const location of variant.locations) {
      if (location.available !== 0 && !locations.has(location.id)) {
        locations.set(location.id, location.name);
      }
    }
  }

  const priority = ["大阪", "東京"];
  const locationColumns = [...locations]
    .map(([id, name]) => ({ id, name, isTotal: false }))
    .sort((left, right) => {
      const rank = (name: string): number => {
        const index = priority.indexOf(inventoryLocationDisplayName(name));
        return index === -1 ? priority.length : index;
      };
      return rank(left.name) - rank(right.name)
        || left.name.localeCompare(right.name, "ja");
    });

  return [
    { id: "__total__", name: "販売可能合計", isTotal: true },
    ...locationColumns,
  ];
}

function locationSort([left]: [string, number], [right]: [string, number]): number {
  const priority = ["大阪", "東京"];
  const leftPriority = priority.indexOf(left);
  const rightPriority = priority.indexOf(right);
  if (leftPriority !== rightPriority) {
    return (leftPriority === -1 ? priority.length : leftPriority)
      - (rightPriority === -1 ? priority.length : rightPriority);
  }
  return left.localeCompare(right, "ja");
}

export function formatInventoryConsumptionSnapshot(
  locations: readonly InventoryConsumptionSnapshot[],
): string {
  const quantities = new Map<string, number>();
  for (const location of locations) {
    if (!Number.isInteger(location.quantity) || location.quantity <= 0) continue;
    const name = inventoryLocationDisplayName(location.name);
    if (!name) continue;
    quantities.set(name, (quantities.get(name) ?? 0) + location.quantity);
  }
  return [...quantities]
    .sort(locationSort)
    .map(([name, quantity]) => name + " (" + quantity + ")")
    .join(", ");
}

export function inventoryConsumptionDisplay(item: {
  consumedInventoryLocations?: readonly InventoryConsumptionSnapshot[];
  inventoryConsumptionSource?: string;
  inventoryConsumptionCapturedAt?: string;
}): string {
  if (isManagedInventorySource(item.inventoryConsumptionSource) && !item.inventoryConsumptionCapturedAt) {
    return "確認中";
  }
  return formatInventoryConsumptionSnapshot(item.consumedInventoryLocations ?? []) || "—";
}

export function inventoryRemainingDisplay(item: {
  inventoryLocations?: readonly InventoryLocationSnapshot[];
  inventoryConsumptionSource?: string;
  inventorySnapshotCapturedAt?: string;
}): string {
  if (isManagedInventorySource(item.inventoryConsumptionSource) && !item.inventorySnapshotCapturedAt) {
    return "確認中";
  }
  return formatInventoryLocationSnapshot(item.inventoryLocations ?? []) || "—";
}

export function formatInventoryLocationSnapshot(
  locations: readonly InventoryLocationSnapshot[],
): string {
  const quantities = new Map<string, number>();
  for (const location of locations) {
    if (!Number.isInteger(location.available) || location.available <= 0) continue;
    const name = inventoryLocationDisplayName(location.name);
    if (!name) continue;
    quantities.set(name, (quantities.get(name) ?? 0) + location.available);
  }
  return [...quantities]
    .sort(locationSort)
    .map(([name, available]) => name + " (" + available + ")")
    .join(", ");
}
